import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserCog } from "lucide-react";

export default function Account() {
  const { user, checkAuth } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState(user?.email || "");
  const [newName, setNewName] = useState(user?.name || "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Current password required");
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword && newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    const payload = { current_password: currentPassword };
    if (newEmail && newEmail !== user?.email) payload.new_email = newEmail;
    if (newName !== user?.name) payload.new_name = newName;
    if (newPassword) payload.new_password = newPassword;

    if (Object.keys(payload).length === 1) {
      toast.info("No changes to save");
      return;
    }

    setBusy(true);
    try {
      await api.post("/auth/change-credentials", payload);
      toast.success("Credentials updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      await checkAuth();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="account-page">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Profile</div>
        <h1 className="text-2xl md:text-3xl font-mono font-semibold tracking-tight">Account Settings</h1>
        <p className="text-sm text-zinc-400 mt-1">Change your email, name and password. Requires current password.</p>
      </div>

      <form onSubmit={submit} className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-6 space-y-5">
        <div className="flex items-center gap-3 pb-3 border-b border-zinc-800">
          <UserCog className="h-5 w-5 text-zinc-400" />
          <div>
            <div className="text-sm font-mono">{user?.email}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">role: {user?.role || "user"}</div>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Display name</Label>
            <Input
              data-testid="account-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono"
              placeholder="Admin"
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Email</Label>
            <Input
              data-testid="account-email-input"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-zinc-800 space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Change password (optional)</div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">New password</Label>
            <Input
              data-testid="account-new-password-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono"
              placeholder="min 6 characters"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Confirm new password</Label>
            <Input
              data-testid="account-confirm-password-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-1 bg-transparent border-zinc-800 rounded-sm font-mono"
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-zinc-800 space-y-2">
          <Label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Current password (required to save)</Label>
          <Input
            data-testid="account-current-password-input"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            className="bg-transparent border-zinc-800 rounded-sm font-mono"
            autoComplete="current-password"
          />
        </div>

        <Button
          data-testid="account-save-btn"
          type="submit"
          disabled={busy}
          className="rounded-sm bg-white text-black hover:bg-zinc-200 font-mono w-full"
        >
          {busy ? "SAVING…" : "SAVE CHANGES"}
        </Button>
      </form>
    </div>
  );
}
